import {
  AlertTriangle,
  CalendarDays,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";

const inputClass =
  "w-full min-w-[140px] rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const textareaClass =
  "w-full min-w-[230px] resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function SummaryCard({ label, value, icon: Icon, color = "blue" }) {
  const colorStyles = {
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
  };

  return (
    <div
      className={`rounded-xl border p-5 shadow-sm ${
        colorStyles[color] || colorStyles.blue
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="mt-2 text-2xl font-bold">{value}</p>
        </div>

        <div className="rounded-xl bg-white/70 p-3">
          <Icon size={23} />
        </div>
      </div>
    </div>
  );
}

export default function RiskGovernanceForm({ data, onChange }) {
  const handleExposureChange = (event) => {
    const { name, value } = event.target;

    onChange({
      ...data,
      [name]: value,
    });
  };

  const updateRisk = (index, field, value) => {
    const updatedRisks = data.risks.map((risk, rowIndex) =>
      rowIndex === index
        ? {
            ...risk,
            [field]: value,
          }
        : risk
    );

    onChange({
      ...data,
      risks: updatedRisks,
    });
  };

  const generateRiskId = () => {
    const nextNumber = data.risks.length + 1;
    return `RSK-${String(nextNumber).padStart(2, "0")}`;
  };

  const addRisk = () => {
    onChange({
      ...data,
      risks: [
        ...data.risks,
        {
          id: createRowId(),
          riskId: generateRiskId(),
          classification: "",
          description: "",
          impactTier: "",
          mitigationStrategy: "",
          owner: "",
        },
      ],
    });
  };

  const removeRisk = (index) => {
    const filteredRisks = data.risks.filter(
      (_, rowIndex) => rowIndex !== index
    );

    const renumberedRisks = filteredRisks.map((risk, rowIndex) => ({
      ...risk,
      riskId: `RSK-${String(rowIndex + 1).padStart(2, "0")}`,
    }));

    onChange({
      ...data,
      risks: renumberedRisks,
    });
  };

  const highRiskCount = data.risks.filter(
    (risk) =>
      risk.impactTier === "High" || risk.impactTier === "Critical"
  ).length;

  const mediumRiskCount = data.risks.filter(
    (risk) => risk.impactTier === "Medium"
  ).length;

  const mitigatedRiskCount = data.risks.filter(
    (risk) =>
      risk.mitigationStrategy &&
      risk.mitigationStrategy.trim().length > 0
  ).length;

  return (
    <div className="space-y-6">
      {/* SUMMARY CARDS */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Total Identified Risks"
          value={data.risks.length}
          icon={AlertTriangle}
          color="blue"
        />

        <SummaryCard
          label="High / Critical Risks"
          value={highRiskCount}
          icon={AlertTriangle}
          color="red"
        />

        <SummaryCard
          label="Medium Risks"
          value={mediumRiskCount}
          icon={CalendarDays}
          color="amber"
        />

        <SummaryCard
          label="Mitigation Defined"
          value={`${mitigatedRiskCount}/${data.risks.length}`}
          icon={ShieldCheck}
          color="emerald"
        />
      </div>

      {/* EXPOSURE FREQUENCY */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <h3 className="text-base font-semibold text-slate-900">
            Exposure Frequency Tracker
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Record the expected number of monthly and quarterly risk
            exposure events.
          </p>
        </div>

        <div className="grid gap-5 p-5 md:grid-cols-2">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Monthly Exposure
            </span>

            <div className="relative mt-1.5">
              <input
                type="number"
                name="monthlyExposure"
                value={data.monthlyExposure ?? ""}
                onChange={handleExposureChange}
                min="0"
                step="1"
                placeholder="Enter monthly exposure"
                className={`${inputClass} pr-20`}
              />

              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500">
                Events
              </span>
            </div>

            <p className="mt-1.5 text-xs text-slate-500">
              Expected exposure events during a typical month.
            </p>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Quarterly Exposure
            </span>

            <div className="relative mt-1.5">
              <input
                type="number"
                name="quarterlyExposure"
                value={data.quarterlyExposure ?? ""}
                onChange={handleExposureChange}
                min="0"
                step="1"
                placeholder="Enter quarterly exposure"
                className={`${inputClass} pr-20`}
              />

              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500">
                Events
              </span>
            </div>

            <p className="mt-1.5 text-xs text-slate-500">
              Expected exposure events during a typical quarter.
            </p>
          </label>
        </div>
      </section>

      {/* RISK REGISTER */}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              Actionable Risk Mitigation Register
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Identify bid risks, their impact, mitigation strategy and
              responsible owner.
            </p>
          </div>

          <button
            type="button"
            onClick={addRisk}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
          >
            <Plus size={17} />
            Add Risk
          </button>
        </div>

        <div className="p-5">
          {data.risks.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-center">
              <ShieldCheck className="mx-auto text-slate-400" size={32} />

              <p className="mt-3 text-sm font-medium text-slate-700">
                No risks have been added
              </p>

              <p className="mt-1 text-sm text-slate-500">
                Select Add Risk to create the first risk-register entry.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full min-w-[1500px]">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="min-w-[120px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Risk ID
                    </th>

                    <th className="min-w-[180px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Risk Classification
                    </th>

                    <th className="min-w-[340px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Description / Pitfall
                    </th>

                    <th className="min-w-[150px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Impact Tier
                    </th>

                    <th className="min-w-[380px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Mitigation Strategy
                    </th>

                    <th className="min-w-[190px] px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Owner
                    </th>

                    <th className="w-20 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 bg-white">
                  {data.risks.map((risk, index) => (
                    <tr key={risk.id || index} className="align-top">
                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={risk.riskId}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "riskId",
                              event.target.value
                            )
                          }
                          className={`${inputClass} font-semibold text-blue-700`}
                          placeholder="RSK-01"
                        />
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={risk.classification}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "classification",
                              event.target.value
                            )
                          }
                          placeholder="Enter classification"
                          className={inputClass}
                        />
                      </td>

                      <td className="px-3 py-3">
                        <textarea
                          value={risk.description}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "description",
                              event.target.value
                            )
                          }
                          rows={3}
                          placeholder="Describe the identified risk or potential pitfall"
                          className={textareaClass}
                        />
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="text"
                          list={`impact-tier-options-${index}`}
                          value={risk.impactTier ?? ""}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "impactTier",
                              event.target.value
                            )
                          }
                          placeholder="Select impact tier"
                          className={inputClass}
                        />
                        <datalist id={`impact-tier-options-${index}`}>
                          <option value="High" />
                          <option value="Medium" />
                          <option value="Low" />
                        </datalist>
                      </td>

                      <td className="px-3 py-3">
                        <textarea
                          value={risk.mitigationStrategy}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "mitigationStrategy",
                              event.target.value
                            )
                          }
                          rows={3}
                          placeholder="Describe the action that will reduce or prevent this risk"
                          className={textareaClass}
                        />
                      </td>

                      <td className="px-3 py-3">
                        <input
                          type="text"
                          value={risk.owner}
                          onChange={(event) =>
                            updateRisk(
                              index,
                              "owner",
                              event.target.value
                            )
                          }
                          placeholder="Responsible person or team"
                          className={inputClass}
                        />
                      </td>

                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => removeRisk(index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-600 transition hover:bg-red-100"
                          title="Remove risk"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* GOVERNANCE SUMMARY */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900">
          Governance Summary
        </h3>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-500">
              Risk Register Completion
            </p>

            <p className="mt-1 text-xl font-bold text-slate-900">
              {data.risks.length > 0
                ? Math.round(
                    (mitigatedRiskCount / data.risks.length) * 100
                  )
                : 0}
              %
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-500">
              Risks Requiring Attention
            </p>

            <p
              className={`mt-1 text-xl font-bold ${
                highRiskCount > 0 ? "text-red-700" : "text-emerald-700"
              }`}
            >
              {highRiskCount}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-500">
              Total Risk Owners Assigned
            </p>

            <p className="mt-1 text-xl font-bold text-slate-900">
              {
                data.risks.filter(
                  (risk) => risk.owner && risk.owner.trim().length > 0
                ).length
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
